export interface IRouteGraphSegment {
  startStopId: number;
  endStopId: number;
  weightSeconds: number;
}

export interface IRouteGraphRequest {
  startStopId: number;
  endStopId: number | null;
  segments: IRouteGraphSegment[];
  // Skips the coverage search; coverageSeconds comes back null
  shortestPathOnly?: boolean;
}

export interface IRouteGraphResult {
  shortestPathStopIds: number[] | null;
  shortestPathSeconds: number | null;
  coverageStopIds: number[] | null;
  coverageSeconds: number | null;
  coverageApproximate: boolean;
}

export const EXACT_SEARCH_MAX_STOPS = 16;
const UNREACHABLE = Number.MAX_SAFE_INTEGER;

export function computeRouteGraphPaths(graph: IRouteGraphRequest): IRouteGraphResult {
  const { startStopId, endStopId, segments, virtualEndStopId } = normalizeCyclicGraph(graph);
  const stopIds = new Set<number>([startStopId]);
  if (endStopId != null) stopIds.add(endStopId);
  segments.forEach((segment) => {
    stopIds.add(segment.startStopId);
    stopIds.add(segment.endStopId);
  });
  const paths = new ShortestPaths([...stopIds].sort((first, second) => first - second), segments);
  const withRealEndStop = (ids: number[] | null) =>
    ids?.map((stopId) => (stopId === virtualEndStopId ? startStopId : stopId)) ?? null;

  const shortestSeconds = endStopId == null ? 0 : paths.secondsBetween(startStopId, endStopId);
  if (shortestSeconds == null) {
    return { shortestPathStopIds: null, shortestPathSeconds: null, coverageStopIds: null, coverageSeconds: null, coverageApproximate: false };
  }
  const shortestPathStopIds = endStopId == null ? null : withRealEndStop(paths.pathBetween(startStopId, endStopId));
  if (graph.shortestPathOnly) {
    return { shortestPathStopIds, shortestPathSeconds: shortestSeconds, coverageStopIds: null, coverageSeconds: null, coverageApproximate: false };
  }
  const coverage = new CoverageRoutes(paths, startStopId, endStopId, paths.stopIds);
  return {
    shortestPathStopIds,
    shortestPathSeconds: shortestSeconds,
    coverageStopIds: withRealEndStop(coverage.longestWalk),
    coverageSeconds: coverage.longestSeconds,
    coverageApproximate: coverage.approximate,
  };
}

// A cyclic route flags the same stop as start and end (or loops back to the start with no
// end flag). Mirroring the backend graph builder, the loop-closing segments are redirected
// to a virtual final stop so paths measure the full loop instead of the trivial zero path.
function normalizeCyclicGraph(graph: IRouteGraphRequest): IRouteGraphRequest & { virtualEndStopId: number | null } {
  const closesLoop = (segment: IRouteGraphSegment) => segment.endStopId === graph.startStopId;
  const isCyclic = graph.endStopId === graph.startStopId || (graph.endStopId == null && graph.segments.some(closesLoop));
  if (!isCyclic) return { ...graph, virtualEndStopId: null };
  const virtualEndStopId = graph.segments.reduce((lowestId, segment) =>
    Math.min(lowestId, segment.startStopId, segment.endStopId), graph.startStopId) - 1;
  return {
    startStopId: graph.startStopId,
    endStopId: virtualEndStopId,
    segments: graph.segments.map((segment) => (closesLoop(segment) ? { ...segment, endStopId: virtualEndStopId } : segment)),
    virtualEndStopId,
  };
}

class ShortestPaths {
  private readonly indexByStopId = new Map<number, number>();
  private readonly seconds: number[][];
  private readonly predecessor: number[][];

  constructor(readonly stopIds: number[], segments: IRouteGraphSegment[]) {
    stopIds.forEach((stopId, index) => this.indexByStopId.set(stopId, index));
    const size = stopIds.length;
    this.seconds = stopIds.map((_, index) => stopIds.map((__, other) => (index === other ? 0 : UNREACHABLE)));
    this.predecessor = stopIds.map(() => stopIds.map(() => -1));
    segments.forEach((segment) => {
      const from = this.indexByStopId.get(segment.startStopId)!;
      const to = this.indexByStopId.get(segment.endStopId)!;
      if (segment.weightSeconds < this.seconds[from][to]) {
        this.seconds[from][to] = segment.weightSeconds;
        this.predecessor[from][to] = from;
      }
    });
    for (let via = 0; via < size; via++) {
      for (let from = 0; from < size; from++) {
        if (this.seconds[from][via] === UNREACHABLE) continue;
        for (let to = 0; to < size; to++) {
          const candidate = this.seconds[from][via] + this.seconds[via][to];
          if (candidate < this.seconds[from][to]) {
            this.seconds[from][to] = candidate;
            this.predecessor[from][to] = this.predecessor[via][to];
          }
        }
      }
    }
  }

  secondsBetween(fromStopId: number, toStopId: number): number | null {
    const seconds = this.seconds[this.indexByStopId.get(fromStopId)!][this.indexByStopId.get(toStopId)!];
    return seconds >= UNREACHABLE ? null : seconds;
  }

  pathBetween(fromStopId: number, toStopId: number): number[] {
    if (this.secondsBetween(fromStopId, toStopId) == null) return [];
    const from = this.indexByStopId.get(fromStopId)!;
    const path: number[] = [];
    let current = this.indexByStopId.get(toStopId)!;
    while (current !== from) {
      path.unshift(this.stopIds[current]);
      current = this.predecessor[from][current];
    }
    path.unshift(fromStopId);
    return path;
  }
}

// The latest arrival at every stop over the coverage routes: for each coverable subset of stops, the
// shortest walk start→end serving all of them. Exact over all subsets up to EXACT_SEARCH_MAX_STOPS
// intermediate stops, greedy (nearest neighbour + 2-opt over still-uncovered stops) above.
class CoverageRoutes {
  longestSeconds: number | null = null;
  longestWalk: number[] | null = null;
  readonly approximate: boolean;
  private readonly coverable: number[];

  constructor(
    private readonly paths: ShortestPaths,
    private readonly startStopId: number,
    private readonly endStopId: number | null,
    stopIds: number[],
  ) {
    this.coverable = stopIds.filter((stopId) =>
      stopId !== startStopId && stopId !== endStopId &&
      paths.secondsBetween(startStopId, stopId) != null &&
      (endStopId == null || paths.secondsBetween(stopId, endStopId) != null));
    this.approximate = this.coverable.length > EXACT_SEARCH_MAX_STOPS;
    if (endStopId == null || paths.secondsBetween(startStopId, endStopId) != null) this.recordWalk(this.walkThrough([]));
    if (this.approximate) this.recordGreedyWalks();
    else this.recordEverySubsetWalk();
  }

  private secondsOrUnreachable(fromStopId: number, toStopId: number): number {
    return this.paths.secondsBetween(fromStopId, toStopId) ?? UNREACHABLE;
  }

  private tailSeconds(stopId: number): number {
    return this.endStopId == null ? 0 : this.secondsOrUnreachable(stopId, this.endStopId);
  }

  private walkThrough(orderedStopIds: number[]): number[] {
    return [this.startStopId, ...orderedStopIds, ...(this.endStopId == null ? [] : [this.endStopId])];
  }

  private recordWalk(keyStopIds: number[]): Set<number> {
    const visited = new Set<number>([this.startStopId]);
    const walk = [this.startStopId];
    let elapsed = 0;
    for (let index = 1; index < keyStopIds.length; index++) {
      const leg = this.paths.pathBetween(keyStopIds[index - 1], keyStopIds[index]);
      for (let step = 1; step < leg.length; step++) {
        elapsed += this.secondsOrUnreachable(leg[step - 1], leg[step]);
        visited.add(leg[step]);
        walk.push(leg[step]);
      }
    }
    if (this.longestSeconds == null || elapsed > this.longestSeconds) {
      this.longestSeconds = elapsed;
      this.longestWalk = walk;
    }
    return visited;
  }

  private recordEverySubsetWalk() {
    const stops = this.coverable;
    const size = stops.length;
    if (size === 0) return;
    const subsets = 1 << size;
    const cost = Array.from({ length: subsets }, () => new Array<number>(size).fill(UNREACHABLE));
    const parent = Array.from({ length: subsets }, () => new Array<number>(size).fill(-1));
    for (let stop = 0; stop < size; stop++) cost[1 << stop][stop] = this.secondsOrUnreachable(this.startStopId, stops[stop]);
    for (let mask = 1; mask < subsets; mask++) {
      for (let last = 0; last < size; last++) {
        if ((mask & (1 << last)) === 0 || cost[mask][last] >= UNREACHABLE) continue;
        for (let next = 0; next < size; next++) {
          if ((mask & (1 << next)) !== 0) continue;
          const candidate = cost[mask][last] + this.secondsOrUnreachable(stops[last], stops[next]);
          const nextMask = mask | (1 << next);
          if (candidate < cost[nextMask][next]) {
            cost[nextMask][next] = candidate;
            parent[nextMask][next] = last;
          }
        }
      }
    }
    for (let mask = 1; mask < subsets; mask++) {
      let bestLast = -1;
      let bestCost = UNREACHABLE;
      for (let last = 0; last < size; last++) {
        if ((mask & (1 << last)) === 0 || cost[mask][last] >= UNREACHABLE) continue;
        const tail = this.tailSeconds(stops[last]);
        if (tail >= UNREACHABLE) continue;
        if (cost[mask][last] + tail < bestCost) {
          bestCost = cost[mask][last] + tail;
          bestLast = last;
        }
      }
      if (bestLast < 0) continue;
      const order: number[] = [];
      let currentMask = mask;
      let current = bestLast;
      while (current >= 0) {
        order.unshift(stops[current]);
        const previous = parent[currentMask][current];
        currentMask &= ~(1 << current);
        current = previous;
      }
      this.recordWalk(this.walkThrough(order));
    }
  }

  private recordGreedyWalks() {
    const uncovered = new Set<number>(this.coverable);
    while (uncovered.size > 0) {
      const order = this.nearestNeighbourOrder(uncovered);
      if (order.length === 0) break;
      this.improveByTwoOpt(order);
      this.recordWalk(this.walkThrough(order)).forEach((stopId) => uncovered.delete(stopId));
      order.forEach((stopId) => uncovered.delete(stopId));
    }
  }

  private nearestNeighbourOrder(candidates: Set<number>): number[] {
    const order: number[] = [];
    const remaining = [...candidates].sort((first, second) => first - second);
    let current = this.startStopId;
    while (remaining.length > 0) {
      let nearestIndex = -1;
      let nearestSeconds = UNREACHABLE;
      remaining.forEach((candidate, index) => {
        const seconds = this.secondsOrUnreachable(current, candidate);
        if (seconds < nearestSeconds) {
          nearestSeconds = seconds;
          nearestIndex = index;
        }
      });
      if (nearestIndex < 0) break;
      current = remaining.splice(nearestIndex, 1)[0];
      order.push(current);
    }
    return order;
  }

  private improveByTwoOpt(order: number[]) {
    let best = this.orderSeconds(order);
    let improved = true;
    while (improved) {
      improved = false;
      for (let first = 0; first < order.length - 1; first++) {
        for (let last = first + 1; last < order.length; last++) {
          reverseRange(order, first, last);
          const candidate = this.orderSeconds(order);
          if (candidate < best) {
            best = candidate;
            improved = true;
          } else {
            reverseRange(order, first, last);
          }
        }
      }
    }
  }

  private orderSeconds(order: number[]): number {
    let total = 0;
    let current = this.startStopId;
    for (const stopId of order) {
      const leg = this.secondsOrUnreachable(current, stopId);
      if (leg >= UNREACHABLE) return UNREACHABLE;
      total += leg;
      current = stopId;
    }
    const tail = this.tailSeconds(current);
    return tail >= UNREACHABLE ? UNREACHABLE : total + tail;
  }
}

function reverseRange(values: number[], first: number, last: number) {
  while (first < last) {
    [values[first], values[last]] = [values[last], values[first]];
    first++;
    last--;
  }
}
